<script lang="ts">
const tableHeaders = [
  { text: 'Side' },
  { text: 'Ticker' },
  { text: 'CUSIP' },
  { text: 'Open Qty' },
  { text: 'Fee', align: 'end' },
  { text: 'Rebate Rate', align: 'end' },
  { text: 'Exec Qty', align: 'end' },
  { text: 'Total Qty', align: 'end' },
  { text: 'Total Value', align: 'end' },
  { text: 'Time in force', align: 'end' },
];
</script>

<script setup lang="ts">
import Decimal from 'decimal.js';
import { computed, onMounted, shallowRef } from 'vue';
import { timeInForceAbbr } from '@/modules/marketplace/helpers/marketplace';
import ItemsBatchConfirmationDialog from '@/modules/common/components/ItemsBatchConfirmationDialog.vue';
import { PRICE_PRECISION } from '@/modules/common/constants/precision';
import { getPriceAsString } from '@/utils/helpers/auction-numbers';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { OmsTimeInForceType } from '@/connect/gen/consts/omsconsts_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';

const securitiesStore = useSecuritiesStore();
const marketplaceApiService = useMarketplaceApiService();

const props = defineProps<{ items: FrontendOrder[] }>();

const itemsToBeCanceled = shallowRef<FrontendOrder[]>([]);

const itemsWithTotalValue = computed(() =>
  props.items.map((item) => {
    const security = securitiesStore.getSecurity(item.cusip);
    const totalValue = security
      ? new Decimal(security.lastClosePrice ?? 0).mul(item.quantity.toString())
      : new Decimal(0);
    return { ...item, security, totalValue };
  })
);

const description = computed(() => {
  const orderStr = itemsToBeCanceled.value.length > 1 ? `orders` : 'order';
  if (itemsToBeCanceled.value.length > 0) {
    return `You are about to fully cancel ${itemsToBeCanceled.value.length} ${orderStr}`;
  }
  return '';
});

async function execute(): Promise<{ ids?: string[] }> {
  const orderRefs = itemsToBeCanceled.value.map(({ orderRef }) => orderRef);
  const { failedOrderRefs } = await marketplaceApiService.batchCancelOrders(orderRefs);
  return { ids: failedOrderRefs };
}

function formatPrice(price: Decimal): string {
  return getPriceAsString(price, PRICE_PRECISION);
}

function formatTimeInForceType(item: FrontendOrder): string {
  return timeInForceAbbr(OmsTimeInForceType[item.timeInForceType]);
}

onMounted(() => {
  itemsToBeCanceled.value = [...props.items];
});
</script>

<template>
  <ItemsBatchConfirmationDialog
    :description="description"
    :execute="execute"
    item-id-key="orderRef"
    :items="itemsWithTotalValue"
    success-message="Cancel all successfully initiated."
    :table-headers="tableHeaders"
    title="Cancel all"
    v-on="$listeners"
  >
    <template #row="{ item }">
      <td>
        <FormatSide :side="item.side === Side.BORROW ? 'BORROW' : 'LOAN'" />
      </td>
      <td>
        {{ item.security?.ticker }}
      </td>
      <td>
        {{ item.cusip }}
      </td>
      <td class="text-end">
        <PrettyNumber :value="item.quantity - item.totalExecQty" />
      </td>
      <td class="text-end">
        <span v-if="item.fee === null">–</span>
        <RateOutput v-else :rate="item.fee" />
      </td>
      <td class="text-end">
        <span v-if="item.rebate === null">–</span>
        <RateOutput v-else :rate="item.rebate" />
      </td>
      <td class="text-end">
        <span v-if="!item.totalExecQty">–</span>
        <span v-else>
          <PrettyNumber :value="item.totalExecQty" />
        </span>
      </td>
      <td class="text-end">
        <PrettyNumber :value="item.quantity" />
      </td>
      <td class="text-end">${{ formatPrice(item.totalValue) }}</td>
      <td class="text-end">
        {{ formatTimeInForceType(item) }}
      </td>
    </template>
  </ItemsBatchConfirmationDialog>
</template>
